import { API_CONFIG } from '../config/api';
import { AuthenticatedApiService } from './authenticatedApiService';
import { 
  UserServiceResponseModel, 
  CreateUserServiceModel, 
  UpdateUserServiceModel, 
  UserPaginatedResult,
  PaginationParams 
} from '../types';

class UsersApiService extends AuthenticatedApiService {
  /**
   * Get paginated list of users
   */
  async getUsers(params: PaginationParams = { page: 1, pageSize: 10 }): Promise<any> {
    const queryParams = new URLSearchParams({
      page: (params.page || 1).toString(),
      pageSize: (params.pageSize || 10).toString(),
    });

    queryParams.append('tryIgnoreSubscription', 'true');

    // Add search parameter if provided
    if (params.searchTerm && params.searchTerm.trim()) {
      queryParams.append('search', params.searchTerm.trim());
    }

    // Add sorting parameters if provided
    if (params.sortBy && params.sortBy.trim()) {
      queryParams.append('sortBy', params.sortBy.trim());
    }
    if (params.sortDirection && params.sortDirection.trim()) {
      queryParams.append('sortDirection', params.sortDirection.trim());
    }

    // Add date filter parameters if provided
    if (params.startDate) {
      queryParams.append('startDate', params.startDate);
    }
    if (params.lastLoginDate) {
      queryParams.append('lastLoginDate', params.lastLoginDate);
    }

    // Add includeInactive parameter if provided
    if (params.includeInactive !== undefined) {
      queryParams.append('includeInactive', params.includeInactive.toString());
    }

    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}?${queryParams}`;
    
    const response = await this.makeAuthenticatedCall(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Parse the JSON response
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Get user by ID
   */
  async getUserById(id: string): Promise<any> {
    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USER_BY_ID(id)}`;
    
    const response = await this.makeAuthenticatedCall(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Create a new user
   */
  async createUser(userData: CreateUserServiceModel): Promise<any> {
    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}`;
    
    const response = await this.makeAuthenticatedCall(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Update user (PATCH)
   */
  async updateUser(id: string, userData: UpdateUserServiceModel): Promise<any> {
    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USER_BY_ID(id)}`;
    
    const response = await this.makeAuthenticatedCall(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Delete user
   */
  async deleteUser(id: string): Promise<any> {
    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USER_BY_ID(id)}`;
    
    const response = await this.makeAuthenticatedCall(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Toggle user status (Active/Inactive)
   */
  async toggleUserStatus(id: string, isActive: boolean): Promise<any> {
    return this.updateUser(id, { isActive });
  }

  /**
   * Bulk add users
   */
  async bulkAddUsers(bulkData: {
    subscriptionId: string;
    users: Array<{
      firstName: string;
      lastName: string;
      email: string;
      roleId: string;
      teamId?: string;
    }>;
    sendWelcomeEmail: boolean;
  }): Promise<any> {
    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}/bulk`;
    
    const response = await this.makeAuthenticatedCall(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bulkData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Bulk update users (PATCH - only send changed fields)
   */
  async bulkUpdateUsers(bulkData: {
    users: Array<{
      userId: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      roleId?: string;
      isActive?: boolean;
      isBlocked?: boolean;
    }>;
  }): Promise<any> {
    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USERS}/bulk`;
    
    const response = await this.makeAuthenticatedCall(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bulkData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Initialize the service with tokens and refresh callback
   */
  initialize(tokens: any, onTokenRefresh: (tokens: any) => void): void {
    this.setTokens(tokens, onTokenRefresh);
  }
}

// Export singleton instance
export const usersService = new UsersApiService();
